import type { LevelDefinition } from "@/levels/types";
import { computeStars } from "./stars";

/** A completed Campaign run's three raw score components, straight from the engine snapshot/HUD math - never re-derived here. */
export interface CampaignRunComponents {
  biteScore: number;
  skillScore: number;
  paceBonus: number;
}

export interface CampaignCompletionResult {
  finalScore: number;
  stars: 1 | 2 | 3;
}

/**
 * The single authoritative place a completed Campaign run's Final Score
 * and earned stars are derived from its three components. `GameScene`
 * calls this exactly once per completion (both the real and QA-only
 * paths) and feeds the same result to the HUD/results overlay,
 * `ScoreV2Storage`, the Campaign star record, and the server submission -
 * so none of them can ever disagree about what "this run's result" was.
 * Deliberately pure and framework-free (no Phaser) so this contract is
 * unit-testable without a Scene test harness - see
 * `campaignCompletion.test.ts`.
 *
 * Only ever call this with a real, shipped Campaign level (one from
 * `LevelRegistry`) - `computeStars` throws for any other id, and Daily
 * runs never reach this function at all (`GameScene` routes Daily
 * completions to `finishDailyAttempt()` before this point).
 */
export function computeCampaignCompletionResult(level: LevelDefinition, components: CampaignRunComponents): CampaignCompletionResult {
  const finalScore = components.biteScore + components.skillScore + components.paceBonus;
  return { finalScore, stars: computeStars(level, finalScore) };
}
